import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Wordmark } from "@/components/Wordmark";
import { Seo } from "@/components/Seo";
import { submitProduction } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { LEGAL_LINKS } from "@/lib/legalLinks";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";

/* ---------------------------------------------------------------------------
   Executive Production — a Copper & Vine sub-brand microsite.
   Built on the live Copper & Vine tokens (ink #1f1b16, cream #f6f2ec,
   copper #a6633c, vine #34433a) per the brand-match rule, following the
   editorial layout / component spec. Unified title system: Fraunces
   (--cv-font-display); body Newsreader; kickers/labels Jost. Em dashes are
   used only where the client explicitly requested them in the copy.
--------------------------------------------------------------------------- */

const PROBLEM = [
  "You have a conference to produce and no one to actually run the room.",
  "You're walking into someone else's event and want every meeting, dinner, and intro to count.",
  "You have a plan, maybe the one your AI wrote, and need it executed by people who have done it before.",
];

const PROOF = [
  {
    title: "Activation · New York Tech Week · five years running",
    metric: "Across five editions: 28 meetings set · 5 partnerships closed · 20 new contracts · 5 investor leads · 3 dinners",
    body: "We've run the full week at New York Tech Week every year for five years, brand presence, the networking map, the sales run of show, and the dinners. Across those five editions: 28 meetings set, 5 partnerships closed, 20 new contracts, and 5 investor leads. The client shows up; the meetings, the rooms, and the deals are already set.",
  },
  {
    title: "Conference Production · University of Minnesota National Conference · 2009",
    metric: "500 attendees · 22 speakers · 16 sponsors run",
    body: "Full conference production, run end to end, run of show, stage, speakers, sponsors, the room. The client walked in as the host; we ran everything behind it. We've been producing rooms at this scale since 2009.",
  },
  {
    title: "Operator Room · co-located at Dreamforce · 2023",
    metric: "50 data leaders and executives · 10 leads · 5 new deals",
    body: "We convened and ran a curated room of senior data leaders during Dreamforce, the guest list, the room, and the conversation. People left with decisions made, and the room produced pipeline.",
  },
  {
    title: "Go-to-Market · 90 days",
    metric: "5 deals at $35K+ closed in-window · repeatable sales template + SOP growth playbook",
    body: "Positioning, the funnel, the sales choreography, built and run, not filed in a deck. First five deals closed inside the window, with a repeatable template and an SOP-driven growth playbook to keep the motion compounding after we left.",
  },
  {
    title: "Team · 46 people across 10 global offices · 6-week embedded sprint",
    metric: "Launch shipped · new market entered · tech audit passed · key-person risk removed",
    body: "Embedded execution that brought a 46-person team, spread across ten offices worldwide, up to a senior operating standard, and built the coalition to hold it. In six weeks: a launch shipped, a new market entered, a technical audit passed, and key-person risk removed.",
  },
];

const CALENDAR_2026 = [
  "Working Session · Salesforce Tower, Chicago · 2026",
  "Operator Dinner · Washington DC · 2026",
  "Winter Strategy Retreat · 2026",
];

function Eyebrow({ children, className = "" }) {
  return (
    <div className={className}>
      <p className="cv-kicker" style={{ color: "#a6633c" }}>{children}</p>
      <div className="mt-3 h-px w-6" style={{ background: "#a6633c" }} aria-hidden="true" />
    </div>
  );
}

export default function ExecutiveProduction() {
  const reduce = useReducedMotion();
  const formRef = useRef(null);

  const fade = {
    initial: { opacity: 0, y: reduce ? 0 : 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.25, ease: "easeOut" },
  };

  const scrollToForm = () => {
    track("production_cta", { location: "hero" });
    formRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <div data-testid="executive-production" className="ep-scope ep-page bg-[#f6f2ec] text-[#1f1b16] min-h-screen">
      <Seo
        title="Executive Production — Launch & Activation by Copper & Vine"
        description="Executive Production by Copper & Vine: we take the plan to the ground. Conferences, go-to-market, and live activation, produced and executed end to end by a senior team."
        path="/executive-production"
        type="website"
      />
      {/* NAV — slim, sticky, hairline bottom */}
      <header className="sticky top-0 z-40 bg-[#f6f2ec]/95 backdrop-blur-sm border-b border-[#e3dacb]">
        <div className="cv-container flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Wordmark />
            <span className="hidden sm:inline cv-sans uppercase tracking-[0.14em] text-[0.6rem] text-[#a6633c] border-l border-[#e3dacb] pl-3">
              Executive Production
            </span>
          </div>
          <button data-testid="ep-header-cta" onClick={scrollToForm} className="cv-btn-primary text-xs whitespace-nowrap">
            Start a production conversation
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="cv-section">
        <div className="cv-container">
          <motion.div {...fade}>
            <p className="cv-kicker" style={{ color: "#a6633c" }}>Copper &amp; Vine · Executive Production</p>
            <h1 data-testid="ep-hero-headline" className="cv-display-serif text-4xl sm:text-5xl lg:text-6xl mt-7 leading-[1.05] max-w-4xl">
              Your AI gave you the plan. We make it real.
            </h1>
            <p className="cv-body text-lg sm:text-xl text-[#5a5249] mt-7 max-w-[640px]">
              Conferences, go-to-market, and live activation, produced and executed end to end. You bring the
              strategy, or the AI that wrote it. We validate it, refine it, and run it in the real world.
            </p>
            <button data-testid="ep-hero-cta" onClick={scrollToForm} className="cv-btn-primary inline-flex items-center gap-2 mt-9">
              Start a production conversation <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="cv-section pt-0">
        <div className="cv-container">
          <motion.div {...fade} className="max-w-3xl">
            <h2 className="cv-display-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
              For the people who know what needs to happen and need someone to run it.
            </h2>
            <div className="mt-10 divide-y divide-[#e3dacb] border-y border-[#e3dacb]">
              {PROBLEM.map((line, i) => (
                <div key={i} data-testid="ep-problem-line" className="flex gap-5 py-6">
                  <span className="cv-serif text-3xl leading-none text-[#a6633c] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="cv-body text-base sm:text-lg text-[#1f1b16] pt-1">{line}</p>
                </div>
              ))}
            </div>
            <p className="cv-sans uppercase tracking-[0.12em] text-xs text-[#5a5249] mt-7">
              Founders, startups, SMBs, mid-market, and enterprise teams. We produce, we activate, we execute.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE RUN */}
      <section className="cv-section pt-0">
        <div className="cv-container">
          <motion.div {...fade}>
            <Eyebrow>What we run</Eyebrow>
          </motion.div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Offering
              testid="ep-offering-event"
              name="Event Production"
              what="Full conference and summit execution. Run of show, stage, speakers, sponsors, the room."
              outcome="You walk in as the host. We run everything behind it."
              fade={fade}
            />
            <Offering
              testid="ep-offering-gtm"
              name="Go-to-Market"
              what="The GTM motion built and run. Positioning, the funnel, the sales choreography. Executed, not just advised."
              outcome="A plan you can watch working, not a deck you file."
              fade={fade}
            />
          </div>

          {/* Activation centerpiece — full width, vine left-rule */}
          <motion.div
            {...fade}
            data-testid="ep-offering-activation"
            className="ep-card mt-5 p-8 sm:p-10 border-l-4"
            style={{ borderLeftColor: "var(--cv-vine)" }}
          >
            <div className="flex items-center gap-3">
              <p className="cv-kicker" style={{ color: "var(--cv-vine)" }}>Activation</p>
              <span className="cv-sans uppercase tracking-[0.12em] text-[0.58rem] text-[#5a5249]/70">The centerpiece</span>
            </div>
            <h3 className="cv-display-serif text-3xl sm:text-4xl mt-4 max-w-2xl leading-tight">
              Your week at someone else's conference, choreographed.
            </h3>
            <p className="cv-body text-base sm:text-lg text-[#5a5249] mt-5 max-w-2xl">
              Brand presence, the networking map, the sales run of show, and the dinners and opportunities
              executed on your behalf.
            </p>
            <div className="h-px w-full bg-[#e3dacb] my-7" />
            <p className="cv-sans uppercase tracking-[0.12em] text-[0.62rem] text-[#5a5249]/70">Outcome</p>
            <p className="cv-quote text-xl sm:text-2xl text-[#1f1b16] mt-2 max-w-2xl">
              You show up. The meetings, the rooms, and the deals are already set.
            </p>
          </motion.div>

          <div className="mt-5">
            <Offering
              testid="ep-offering-team"
              name="Team Workshops & Team Management"
              what="We make your team as good as us, or we embed alongside them until they are. Even when the team is AI."
              sub="Workshops that lift your people, or embedded execution that brings a lean or AI-augmented team up to a senior standard."
              outcome="Your team operates at the level the room demands."
              fade={fade}
              full
            />
          </div>
        </div>
      </section>

      {/* THESIS — the emotional center, extra air */}
      <section className="py-24 sm:py-32">
        <div className="cv-container">
          <div className="h-px w-full bg-[#e3dacb]" />
          <motion.div {...fade} className="max-w-[720px] py-14">
            <h2 className="cv-display-serif text-3xl sm:text-4xl leading-tight">
              Anyone can get the plan. Almost no one can run it.
            </h2>
            <p className="cv-body text-lg text-[#5a5249] mt-7">
              AI will hand you everything. The conference strategy, the GTM motion, the week-long activation
              plan. The gap has never been the plan. It's the execution. We take what your AI told you to do,
              validate what holds up, cut what doesn't, and run the rest with people who have produced these
              rooms before. The plan is the easy part. The execution is the work.
            </p>
          </motion.div>
          <div className="h-px w-full bg-[#e3dacb]" />
        </div>
      </section>

      {/* PROOF */}
      <section className="cv-section pt-0">
        <div className="cv-container">
          <motion.div {...fade}>
            <Eyebrow>Proof</Eyebrow>
            <h2 className="cv-display-serif text-2xl sm:text-3xl mt-5">Rooms we've produced and run.</h2>
          </motion.div>

          {/* Martha's Vineyard — full-width hero proof */}
          <motion.div {...fade} data-testid="ep-proof-vineyard" className="ep-card mt-8 p-8 sm:p-10">
            <h3 className="cv-display-serif text-xl sm:text-2xl text-[#1f1b16] leading-snug">
              Activation House · Martha's Vineyard · 11 years running
            </h3>
            <p className="cv-sans text-base text-[#a6633c] font-medium mt-3 leading-relaxed">
              200+ founders hosted per season · 25+ partnerships formed · 4+ deals closed in-season at $1M+ combined ARR
            </p>
            <p className="cv-body text-base sm:text-lg text-[#5a5249] mt-4 max-w-3xl">
              Every August we turn a private house into a month-long activation venue, a partnership and meeting
              space engineered to put founders in the rooms and conversations that move their year. A single season
              hosts 200+ founders and forms 25+ partnerships, with 11+ founders going on to raise their rounds and
              4+ deals closing in-season at over $1M in combined ARR. Eleven consecutive years. Founders confidential
              by agreement; the access is the product.
            </p>
          </motion.div>

          {/* Remaining five — 2-column grid */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PROOF.map((c, i) => (
              <motion.div
                key={c.title}
                {...fade}
                transition={{ ...fade.transition, delay: reduce ? 0 : i * 0.06 }}
                data-testid="ep-proof-card"
                className="ep-card p-8"
              >
                <h3 className="cv-display-serif text-lg sm:text-xl text-[#1f1b16] leading-snug">{c.title}</h3>
                <p className="cv-sans text-[0.95rem] text-[#a6633c] font-medium mt-3 leading-relaxed">{c.metric}</p>
                <p className="cv-body text-base text-[#5a5249] mt-4">{c.body}</p>
              </motion.div>
            ))}
          </div>

          {/* CES — slim full-width credential bar (distinct, not a card) */}
          <motion.div {...fade} data-testid="ep-ces-bar" className="mt-8 border-y border-[#e3dacb] py-7">
            <p className="cv-kicker" style={{ color: "#a6633c" }}>CES · six consecutive years</p>
            <p className="cv-body text-base sm:text-lg text-[#5a5249] mt-3 max-w-3xl">
              Stage presence and the rooms around it, six years running. The conferences others attend, we've been
              on the program at.
            </p>
          </motion.div>

          {/* 2026 strip — slim linked row to /events */}
          <motion.div {...fade} data-testid="ep-2026-strip" className="mt-8">
            <Link
              to="/events"
              onClick={() => track("door_click", { target: "/events", location: "ep_2026_strip" })}
              className="cv-link inline-flex items-center gap-1.5"
            >
              On the 2026 calendar, see what we're producing <ArrowUpRight size={14} />
            </Link>
            <ul className="mt-4 divide-y divide-[#e3dacb] border-t border-[#e3dacb]">
              {CALENDAR_2026.map((item) => (
                <li key={item}>
                  <Link
                    to="/events"
                    className="flex items-center justify-between gap-4 py-3.5 group"
                    onClick={() => track("door_click", { target: "/events", location: "ep_2026_item" })}
                  >
                    <span className="cv-body text-sm sm:text-base text-[#1f1b16]">{item}</span>
                    <ArrowUpRight size={15} className="text-[#5a5249] group-hover:text-[#a6633c] transition-colors shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CLOSE / CONTACT */}
      <section ref={formRef} className="cv-section pt-0 scroll-mt-20">
        <div className="cv-container">
          <div className="h-px w-full bg-[#e3dacb] mb-16" />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] lg:gap-16">
            <div className="max-w-[720px]">
              <h2 className="cv-display-serif text-3xl sm:text-4xl">Start a production conversation.</h2>
              <p className="cv-body text-lg text-[#5a5249] mt-5">
                Tell us the event, the activation, or the plan you need run. We respond with specifics — scope,
                approach, and what it takes to deliver — and an RFP-ready package.
              </p>
              <ProductionForm />
            </div>
            {/* Quiet right-column line, balances the close */}
            <aside className="hidden lg:block lg:pt-2" data-testid="ep-close-aside">
              <div className="cv-hairline-copper" />
              <p className="cv-kicker mt-5" style={{ color: "#a6633c" }}>What comes back</p>
              <ul className="mt-4 space-y-2.5 cv-body text-sm text-[#5a5249]">
                <li>Capabilities</li>
                <li>Scope and approach</li>
                <li>RFP-ready package</li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#e3dacb]">
        <div className="cv-container py-12">
          <Wordmark />
          <p className="cv-sans uppercase tracking-[0.14em] text-[0.6rem] text-[#a6633c] mt-3">Executive Production</p>
          <p className="cv-body text-sm text-[#5a5249] mt-6">
            Part of Copper &amp; Vine.{" "}
            <Link to="/" className="cv-link normal-case">coppervine.io</Link>
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-5 cv-body text-xs text-[#5a5249]/80">
            <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="hover:text-[#a6633c] transition-colors">Privacy</a>
            <span aria-hidden="true">·</span>
            <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className="hover:text-[#a6633c] transition-colors">Terms</a>
            <span aria-hidden="true">·</span>
            <a href={LEGAL_LINKS.cookies} target="_blank" rel="noopener noreferrer" className="hover:text-[#a6633c] transition-colors">Cookies</a>
            <span aria-hidden="true">·</span>
            <span>© 2026 Copper and Vine.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Offering({ name, what, sub, outcome, testid, fade, full }) {
  return (
    <motion.div {...fade} data-testid={testid} className={`ep-card p-8 ${full ? "" : "h-full"}`}>
      <h3 className="cv-display-serif text-lg">{name}</h3>
      <p className="cv-body text-base text-[#5a5249] mt-3">{what}</p>
      {sub && <p className="cv-body text-base text-[#5a5249]/85 mt-2">{sub}</p>}
      <div className="h-px w-full bg-[#e3dacb] my-5" />
      <p className="cv-sans uppercase tracking-[0.12em] text-[0.62rem] text-[#5a5249]/70">Outcome</p>
      <p className="cv-body italic text-base text-[#1f1b16] mt-1.5">{outcome}</p>
    </motion.div>
  );
}

const EMPTY = { name: "", email: "", company: "", need: "", timeframe: "" };

function ProductionForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Name, email, and company are required.");
      return;
    }
    setStatus("submitting");
    try {
      await submitProduction({ ...form, utm: getUTM() });
      track("production_inquiry", { company: form.company });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="ep-form-success" className="mt-9 flex items-start gap-3 border-l-2 border-[#a6633c] pl-4">
        <span className="w-8 h-8 rounded-full border border-[#a6633c] flex items-center justify-center shrink-0 mt-0.5">
          <Check size={16} className="text-[#a6633c]" />
        </span>
        <p className="cv-body text-base text-[#1f1b16]">
          Received. We'll send our capabilities and an RFP-ready response.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="ep-production-form" onSubmit={submit} className="mt-9 space-y-7" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <Field id="ep-name" label="Name">
          <input id="ep-name" data-testid="ep-name" className="ep-underline" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field id="ep-email" label="Email">
          <input id="ep-email" data-testid="ep-email" type="email" className="ep-underline" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
      </div>
      <Field id="ep-company" label="Company">
        <input id="ep-company" data-testid="ep-company" className="ep-underline" value={form.company} onChange={(e) => set("company", e.target.value)} />
      </Field>
      <Field id="ep-need" label="What do you need produced or executed?">
        <textarea id="ep-need" data-testid="ep-need" rows={3} className="ep-underline resize-none" value={form.need} onChange={(e) => set("need", e.target.value)} />
      </Field>
      <Field id="ep-timeframe" label="When? (event date or window)">
        <input id="ep-timeframe" data-testid="ep-timeframe" className="ep-underline" value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} />
      </Field>
      {error && (
        <p data-testid="ep-form-error" role="alert" className="cv-body text-sm text-[#1f1b16] border-l-2 border-[#a65a43] pl-3">{error}</p>
      )}
      <button data-testid="ep-form-submit" type="submit" disabled={status === "submitting"} className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
        {status === "submitting" ? "Sending..." : <>Send it <ArrowRight size={15} /></>}
      </button>
    </form>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="ep-field">
      <label htmlFor={id} className="ep-label block cv-sans uppercase tracking-[0.12em] text-[0.64rem] text-[#5a5249] transition-colors">
        {label}
      </label>
      {children}
    </div>
  );
}
